// ===== IMPORTS =====
// Core React and Next.js imports
import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {NextSeo} from "next-seo";
import styled from "styled-components";

// Component imports
import {Col, Container, Form, Modal, Row} from "react-bootstrap";
import Title from "../../components/Title";
import Select from "react-select";
import Button from "../../components/Button";
import PlusIcon from "../../components/animations/plus.svg";

// API and Redux imports
import {ApiServices as apiEndPoints, ApiServices} from "../api/network/ApiServices";
import {ApiParamKey} from "../api/network/ApiParamKey";
import {wrapper} from "../api/store";
import {fetchPosts} from "../api/redux/investor";

// Utility imports
import {calculatePortfolioValue, calculatePortfolioValueFromAPI, formatCurrency, formatPercentage} from "../../utils/investmentCalculations";

// Chart library import
import Chart from "chart.js";

// CSS imports
import 'simplebar-react/dist/simplebar.min.css';
import "react-datepicker/dist/react-datepicker.css";

/**
 * Investment Calculator Component
 * Allows users to calculate potential returns on stock investments
 * Supports multiple stocks selection and custom time periods
 */
const InvestorCalculator = () => {
    // ===== REDUX STATE =====
    const getPost = useSelector((state) => state.investorReducer);
    const dispatch = useDispatch();

    // Extract page data from Redux store
    const page_data = getPost?.posts?.data?.page_data;

    // ===== COMPONENT STATE =====
    // UI State
    const [winWidth, setWinWidth] = useState(true); // Track if window width > 767px
    const [can, setCan] = useState(false); // Controls chart visibility

    // Form State
    const [selectedOptions, setSelectedOptions] = useState([]); // Selected stocks
    const [selectedAPIData, setSelectedAPIData] = useState([]); // Selected stocks
    const [selectedOptionsYear, setSelectedOptionsYear] = useState([]); // Selected investment period
    const [payment, setPayment] = useState([]); // Starting amount (unused but kept for compatibility)
    const [principle, setPriciple] = useState(0); // Principal amount for calculations
    const [startDate, setStartDate] = useState(null); // Start date for calculations

    // Modal State
    const [showOthersModal, setShowOthersModal] = useState(false); // Custom year modal visibility
    const [customYear, setCustomYear] = useState(''); // Custom year input value

    // Data State
    const [dropdownOptions, setDropdownOptions] = useState([]); // Available stocks from API
    const [dataOption, setDataOption] = useState([]); // Chart data

    // Error State
    const [modalYearError, setModalYearError] = useState('');
    const [paymentError, setPaymentError] = useState('');
    const [yearError, setYearError] = useState('');

    // Calculation Results State
    const [calculatedFinalValue, setCalculatedFinalValue] = useState(0);
    const [calculatedTotalReturn, setCalculatedTotalReturn] = useState(0);
    const [calculatedCAGR, setCalculatedCAGR] = useState(0);
    const [lastSelectedStocks, setLastSelectedStocks] = useState([]);
    const [lastSelectedYears, setLastSelectedYears] = useState(null);
    const [usedFallbackCalculation, setUsedFallbackCalculation] = useState(false);

    // ===== EFFECTS =====
    /**
     * Initialize component - set up window resize listener and fetch dropdown options
     * To modify: Change breakpoint (767) for mobile/desktop detection
     * To modify: Update API endpoint for stock options
     */
    useEffect(() => {
        // Set initial window width state
        const updateWindowWidth = () => {
            setWinWidth(window.innerWidth > 767);
        };

        updateWindowWidth();
        window.addEventListener("resize", updateWindowWidth);

        // Fetch available stocks from API
        fetchDropdownOptions();

        // Cleanup event listener
        return () => window.removeEventListener("resize", updateWindowWidth);
    }, []);

    // ===== API FUNCTIONS =====
    /**
     * Fetches available stock options from API
     * To modify: Change API endpoint or add additional data processing
     * To modify: Update fallback stock list with current available stocks
     */
    const fetchDropdownOptions = async () => {
        try {
            const response = await fetch('https://cms.shantasecurities.com/api/get-req-data/investor-column');
            const data = await response.json();

            if (data.status === 200 && data.columns) {
                const options = data.columns.map(column => ({
                    value: column,
                    label: column
                }));
                setDropdownOptions(options);
            }
        } catch (error) {
            console.error('Failed to fetch dropdown options:', error);
            // Fallback to hardcoded options if API fails
            // TO MODIFY: Update this list with current available stocks
            setDropdownOptions([
                {value: 'BRACBANK', label: 'BRAC BANK'},
                {value: 'CITYBANK', label: 'CITY BANK'},
                {value: 'SQURPHARMA', label: 'SQUR PHARMA'},
                {value: 'RENATA', label: 'RENATA'},
                {value: 'BXPHARMA', label: 'BX PHARMA'},
                {value: 'MARICO', label: 'MARICO'},
                {value: 'RECKITTBEN', label: 'RECKITTBEN'},
                {value: 'OLYMPIC', label: 'OLYMPIC'},
                {value: 'BATBC', label: 'BATBC'},
                {value: 'LHBL', label: 'LHBL'},
                {value: 'UNILEVERCL', label: 'UNILEVERCL'},
                {value: 'HEIDELBCEM', label: 'HEIDELBCEM'},
                {value: 'LINDEBD', label: 'LINDEBD'},
                {value: 'BERGERPBL', label: 'BERGERPBL'},
                {value: 'GP', label: 'GP'},
                {value: 'UPGDCL', label: 'UPGDCL'},
            ]);
        }
    };

    // ===== UTILITY FUNCTIONS =====
    /**
     * Formats date to YYYY-MM-DD format
     * To modify: Change date format as needed
     */
    const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // ===== EVENT HANDLERS =====
    /**
     * Handles payment input changes and clears errors
     * To modify: Add input validation or formatting
     */
    const handlePayment = (selected) => {
        setPayment(selected);
        clearPaymentError();
    };

    /**
     * Handles stock selection changes
     * To modify: Add validation for maximum selection limit
     */
    const handleSelection = (selected) => {
        setSelectedOptions(selected);
    };

    // UseEffect no longer needed for modal year handling
    // useEffect(() => {
    //     if (!showOthersModal && shouldSubmitAfterYearSet && selectedOptionsYear) {
    //         const syntheticEvent = { preventDefault: () => {} };
    //         handleSubmit(syntheticEvent);
    //         setShouldSubmitAfterYearSet(false);
    //     }
    // }, [showOthersModal, shouldSubmitAfterYearSet, selectedOptionsYear]);

    const handleSelectionYear = (selected) => {
        if (selected?.value === 'others') {
            // Check if starting amount is filled before opening modal
            const paymentValue = document.querySelector('input[name="payment"]')?.value;
            if (!paymentValue || paymentValue.trim() === '') {
                setPaymentError('Please enter starting amount first.');
                return;
            }
            setShowOthersModal(true);
        } else {
            setSelectedOptionsYear(selected?.value);
            clearYearError();
        }
    };

    const handleOthersYearSubmit = async () => {
        const yearNumber = parseInt(customYear);
        if (yearNumber >= 1 && yearNumber <= 15) {
            setShowOthersModal(false);  // Close modal immediately
            setCustomYear('');
            clearYearError();

            // Directly call handleSubmit with the custom year value
            const syntheticEvent = { preventDefault: () => {} };
            handleSubmit(syntheticEvent, customYear);
        }
    };

    /**
     * Handles closing the custom year modal
     * To modify: Add additional cleanup logic if needed
     */
    const handleOthersModalClose = () => {
        setShowOthersModal(false);
        setCustomYear('');
        setModalYearError('');
    };

    // ===== ERROR HANDLING FUNCTIONS =====
    /**
     * Clears payment error messages from both state and DOM
     * To modify: Add additional error clearing logic
     */
    const clearPaymentError = () => {
        if (typeof document !== 'undefined') {
            const errorElement = document.getElementById('paymentError');
            if (errorElement) errorElement.textContent = '';
        }
        setPaymentError('');
    };

    /**
     * Clears year selection error messages from both state and DOM
     * To modify: Add additional error clearing logic
     */
    const clearYearError = () => {
        if (typeof document !== 'undefined') {
            const errorElement = document.getElementById('yearError');
            if (errorElement) errorElement.textContent = '';
        }
        setYearError('');
    };

    const handleSubmit = async (e, overrideYear = null) => {
        e.preventDefault();

        // Reset UI & state
        clearPaymentError();
        clearYearError();
        document.getElementById('paymentError').textContent = '';
        document.getElementById('yearError').textContent = '';
        setDataOption([]);

        const paymentValue = document.querySelector('input[name="payment"]')?.value;
        const selectedOptionsArray = Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions];
        const selectedStocks = selectedOptionsArray.map((option) => option.value);
        const selectedStocksYear = overrideYear || selectedOptionsYear;
        const selectedDate = startDate ? formatDate(startDate) : formatDate(new Date());

        // Input validation
        if (!paymentValue) {
            setPaymentError('Please enter starting amount.');
            return;
        }

        if (!selectedStocksYear || (typeof selectedStocksYear === 'string' && selectedStocksYear.trim() === '') || (Array.isArray(selectedStocksYear) && selectedStocksYear.length < 1)) {
            setYearError('Please select a year.');
            return;
        }

        try {
            setPriciple(paymentValue);
            const principleAmount = parseFloat(paymentValue);
            const updatedDataOption = [];
            const yearParam = Number(selectedStocksYear);

            let final_value = 0;
            let total_return = 0;
            let cagr = 0;
            let useClientSideCalculation = false;

            try {
                // Use the new API endpoint to get investor data with all calculations
                const apiUrl = `https://cms.shantasecurities.com/api/get-req-data/investor-data?stock=${selectedStocks.join(',')}&year=${yearParam}&payment=${paymentValue}`;
                const response = await fetch(apiUrl);
                const apiData = await response.json();
                setCalculatedCAGR(apiData?.csgrFinal?.csgr_percentage)

                console.log('API Response:', apiData);
                setSelectedAPIData(apiData);

                // Check if we have valid API data
                if (apiData && apiData.summary) {
                    // Use the pre-calculated summary values from API
                    final_value = parseFloat(apiData?.totalreturn?.[0].final_value) || 0;
                    total_return = parseFloat(apiData?.totalreturn_with_dividend?.[0]?.total_return_percentage) || 0;
                    cagr = parseFloat(apiData.summary.cagr_with_dividends_pct) || 0;



                    console.log(apiData.data, 'chart data');
                    // Process the daily data for chart visualization
                    if (apiData.
                        portfolioValue && Array.isArray(apiData.
                        portfolioValue) && apiData.
                        portfolioValue.length > 0) {
                        const dailyData = apiData.
                            portfolioValue;
                        let currentPortfolioValue = principleAmount;

                        // Create chart data points from daily returns
                        dailyData.forEach((dayData) => {
                            const date = new Date(dayData.date);
                            let dailyMultiplier = 1;
                            //
                            // console.log(dayData, 'daily data')
                            // // Calculate combined daily multiplier for selected stocks
                            // selectedStocks.forEach((stock) => {
                            //     if (dayData[stock] !== null && dayData[stock] !== undefined) {
                            //         const dailyReturn = parseFloat(dayData[stock]);
                            //         dailyMultiplier *= (1 + dailyReturn);
                            //     }
                            // });
                            //
                            // // Apply to portfolio value
                            // currentPortfolioValue *= dailyMultiplier;

                            updatedDataOption.push({
                                x: date,
                                y: parseFloat(dayData?.value_rounded.toFixed(2))
                            });
                        });
                    } else {
                        // Create simple chart data if no daily data available
                        const startDate = new Date();
                        startDate.setFullYear(startDate.getFullYear() - yearParam);
                        const endDate = new Date();

                        updatedDataOption.push(
                            { x: startDate, y: principleAmount },
                            { x: endDate, y: final_value }
                        );
                    }
                } else {
                    throw new Error('Invalid API response structure - missing summary data');
                }
            } catch (apiError) {
                console.error('API failed, falling back to client-side calculation:', apiError);
                useClientSideCalculation = true;

                // Fallback to client-side calculation
                const portfolioResults = calculatePortfolioValue(principleAmount, selectedStocks, yearParam);

                final_value = portfolioResults.finalValue;
                total_return = portfolioResults.totalReturn;
                cagr = portfolioResults.cagr;


                const currentYear = new Date().getFullYear();
                const currentMonth = new Date().getMonth();
                const startYearClient = currentYear - yearParam + 1;

                portfolioResults.yearlyData.forEach((yearData, index) => {
                    const nextYearData = portfolioResults.yearlyData[index + 1];
                    if (!nextYearData) return;

                    const startValue = yearData.value;
                    const endValue = nextYearData.value;
                    const startYear = startYearClient + yearData.year - 1;
                    const startDate = new Date(startYear, 0, 1);
                    const endDate = new Date(startYear + 1, 0, 1);
                    const totalDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));

                    for (let d = 0; d < totalDays; d++) {
                        const date = new Date(startDate);
                        date.setDate(date.getDate() + d);
                        const interpolatedValue = startValue + ((endValue - startValue) * d) / totalDays;

                        updatedDataOption.push({
                            x: date,
                            y: parseFloat(interpolatedValue.toFixed(2))
                        });
                    }
                });

                const last = portfolioResults.yearlyData.at(-1);
                if (last?.value !== undefined) {
                    updatedDataOption.push({
                        x: new Date(currentYear, currentMonth, 1),
                        y: parseFloat(last.value.toFixed(2))
                    });
                }
            }

            // Sort chart data by date
            updatedDataOption.sort((a, b) => new Date(a.x) - new Date(b.x));
            setDataOption(updatedDataOption);

            console.log('Final Value:', final_value);
            console.log('Total Return:', total_return + '%');
            console.log('CAGR:', cagr + '%');

            setCalculatedFinalValue(final_value);
            setCalculatedTotalReturn(total_return);
            setCalculatedCAGR(cagr);
            setLastSelectedStocks(selectedOptionsArray);
            setLastSelectedYears(selectedStocksYear);
            setUsedFallbackCalculation(useClientSideCalculation);
            setCan(true);

        } catch (error) {
            console.error('Calculation error:', error);
            const resultElement = document.querySelector('.result');
            if (resultElement) {
                resultElement.innerHTML = `
                <div class="result-wrp error">
                    <p>Unable to calculate investment returns. Please try again.</p>
                </div>
            `;
            }
        }
    };




    let uniqueDataOption = [...new Map(dataOption.map(item => [item.x, item])).values()];

    useEffect(() => {
        // Check if dataOption exists and has a length property
        if (dataOption && dataOption.length > 0) {
            function formatDate(dateString) {
                const date = new Date(dateString);
                const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                const month = monthNames[date.getMonth()];
                const year = date.getFullYear();
                return `${month} ${year}`;
            }

            // Sort dataOption by date

            const sortedData = dataOption
                .sort((a, b) => new Date(a.x) - new Date(b.x))
                .map(item => ({
                    ...item,
                    y: typeof item.y === 'string'
                        ? parseFloat(item.y.replace(/,/g, ''))
                        : item.y
                }));

            // Calculate intervals based on the month of each data point
            const intervals = [];
            for (let i = 0; i < sortedData.length - 1; i++) {
                const currentDate = new Date(sortedData[i].x);
                const nextDate = new Date(sortedData[i + 1].x);
                const monthDiff = (nextDate.getFullYear() - currentDate.getFullYear()) * 12 + nextDate.getMonth() - currentDate.getMonth();
                if (monthDiff >= 6) {
                    intervals.push(formatDate(sortedData[i].x));
                }
            }

            // Filter original dataset and create a new dataset with modified x values
            const newData = sortedData.map(item => {
                return { x: formatDate(item.x), y: Number(item.y) };
            });

            (async function () {
                const data = newData;

                new Chart(
                    document.getElementById('line-chart'), {
                        type: 'bar',
                        responsive: true,
                        data: {
                            labels: data.map(row => row.x),
                            datasets: [{
                                backgroundColor: "#978C21",
                                borderColor: "#3182ce",
                                label: 'Investment Calculator',
                                data: data.map(row => row.y)
                            }]
                        },
                        options: {
                            maintainAspectRatio: false,
                            scales: {
                                yAxes: [{
                                    ticks: {
                                        fontSize: 12 // Adjust the font size here
                                    },
                                }],
                                xAxes: [{
                                    ticks: {
                                        fontSize: 14 // Adjust the font size here
                                    }
                                }]
                            },
                            tooltips: {
                                callbacks: {
                                    label: function (tooltipItem, data) {
                                        return 'Portfolio Price: ' + tooltipItem.yLabel;
                                    }
                                }
                            },
                            legend: {
                                labels: {
                                    fontSize: 24, // Adjust the font size for the legend label
                                    fontColor: '#3c3c3b', // Change the font color
                                    fontStyle: 'bold' // Change the font weight
                                }
                            }
                        }
                    }
                );


            })();
        }
    }, [dataOption]);


    const DropdownOptionYear = [
        {value: '2', label: '2 years'},
        {value: '5', label: '5 years'},
        {value: '10', label: '10 years'},
        {value: '15', label: '15 years'},
        {value: 'others', label: 'Others (Write any integer between 1-15)'},
    ];


    const CustomMultiValue = ({data}) => (
        <div>
            <span>{data.label}</span>
            <PlusIcon style={{
                marginLeft: '5px',
                verticalAlign: 'middle',
                marginRight: '5px',
                top: '-2px',
                position: 'relative'
            }}/>
        </div>
    );


    const CustomMultiValueLabel = ({data}) => (
        <div>
            <span>{data.label}</span>
            <PlusIcon style={{
                marginLeft: '5px',
                verticalAlign: 'middle',
                marginRight: '5px',
                top: '-2px',
                position: 'relative'
            }}/>
        </div>
    );





    return (
        <StyledComponent>
            <NextSeo
                title={`${page_data?.title ? page_data?.title + ' | Shanta Securities Limited' : ''}`}
                description={page_data?.meta_description}
                openGraph={{
                    title: page_data?.og_title,
                    description: page_data?.og_description,
                }}
            />

            <div className="investor_calculator pb-200 pt-200">
                <Container>
                    <Row>
                        <Col md={12} className='modal-data__content'>

                            <Row class={'desktop-view'}>
                                <Col className={'form'} md={6}>
                                    <Title margin={'0 0 30px 0'}
                                           text={'Investment Calculator'}/>

                                    <div className="form-wrapper-all">

                                        <Form onSubmit={handleSubmit} method="post"
                                              enctype="multipart/form-data">
                                            <Row>
                                                <Form.Group className="col-md-12 pb-30 form-wrapper">
                                                    <Form.Control
                                                        className={'form-control-lg'}

                                                        type="number"
                                                        name='payment'
                                                        placeholder="Starting Amount"
                                                        required
                                                        onChange={handlePayment}
                                                    />
                                                    <div id="paymentError"
                                                         className="calculator-error-message">{paymentError}</div>

                                                </Form.Group>

                                                <Form.Group className="col-md-12 pb-30 form-wrapper">
                                                    <Select
                                                        isMulti
                                                        placeholder={'Stock'}
                                                        onChange={handleSelection} // replace with your selection handler function
                                                        styles={{
                                                            dropdownIndicator: (provided, state) => ({
                                                                ...provided,
                                                                transform: state.selectProps.menuIsOpen && "rotate(180deg)"
                                                            })
                                                        }}

                                                        classNamePrefix={'react-select'}
                                                        className={'form-select'}
                                                        isSearchable={true}
                                                        isOptionDisabled={() => selectedOptions.length >= 5}
                                                        options={dropdownOptions} // replace with your dropdown options
                                                        theme={(theme) => ({
                                                            ...theme,
                                                            borderRadius: 0,
                                                            colors: {
                                                                ...theme.colors,
                                                                primary25: '#e9e9e9',
                                                                primary: 'black',
                                                            },
                                                        })}
                                                    />
                                                    <p className={'hints'}>Select up to 5 Stocks</p>

                                                </Form.Group>


                                                <Form.Group className="col-md-12 pb-30 form-wrapper last">

                                                    <Select

                                                        placeholder={'Year'}
                                                        name="year"
                                                        onChange={handleSelectionYear} // replace with your selection handler function
                                                        styles={{
                                                            dropdownIndicator: (provided, state) => ({
                                                                ...provided,
                                                                transform: state.selectProps.menuIsOpen && "rotate(180deg)"
                                                            })
                                                        }}
                                                        classNamePrefix={'react-select'}
                                                        className={'form-select'}
                                                        options={DropdownOptionYear} // replace with your dropdown options
                                                        theme={(theme) => ({
                                                            ...theme,
                                                            borderRadius: 0,
                                                            colors: {
                                                                ...theme.colors,
                                                                primary25: '#e9e9e9',
                                                                primary: 'black',
                                                            },
                                                        })}
                                                    />
                                                    <div id="yearError" className="calculator-error-message">{yearError}</div>

                                                </Form.Group>

                                                <div className="clear"></div>

                                                <Form.Group className="col-xl-6 widthHalf"
                                                            onClick={handleSubmit}>
                                                    <Button
                                                        margin={'0 40px 0 0'}
                                                        padding={'0'}
                                                        hoverColor={'#3C3C3B'}
                                                        hoverBackground={'#3C3C3B'}
                                                        width={'auto'}
                                                        text={'Calculate'}
                                                        classname={'col-md-6'}
                                                    />
                                                </Form.Group>
                                            </Row>
                                        </Form>

                                    </div>
                                </Col>

                                <Col md={{ span: 5, offset: 1 }} id={'result'} className={'result'}>
                                    <div className="result-wrp">
                                        {calculatedFinalValue !== null && calculatedFinalValue !== 0 && (
                                            <div>
                                                <p>
                                                    If you invested equally in {lastSelectedStocks.map(stock => stock.label).join(', ')}, {lastSelectedYears} years ago, today you would have had
                                                </p>
                                                <h3>{formatCurrency(calculatedFinalValue)}</h3>
                                                <p>Total Return</p>
                                                <h3>{formatPercentage(calculatedTotalReturn)}</h3>
                                                <p>CAGR</p>
                                                <h3>{formatPercentage(calculatedCAGR)}</h3>
                                                {usedFallbackCalculation && (
                                                    <p className="calculation-note"><small>* Calculated using estimated returns</small></p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            <Row className={'desktop-view-blog'}>
                                {dataOption?.[0] &&
                                    <Col md={12}>
                                        <br/>
                                        <div className="relative chart-container h-350-px">
                                            <canvas id="line-chart"></canvas>
                                        </div>

                                    </Col>
                                }

                                <Col className={'invest-content'} md={12}>


                                    <Title noanim margin={'0 0 30px 0'}
                                           text={'Using Investment Calculator'}/>


                                    <ul>
                                        <li>
                                            <span>Enter the amount</span> you wish you had invested a certain number of
                                            years ago.
                                        </li>
                                        <li>
                                            <span>Select up to five different types</span> of stocks for your investment
                                            during that period. The specified investment will be evenly distributed
                                            among the chosen stocks. You could even opt to select just one stock as
                                            well.
                                        </li>
                                        <li>
                                            <span>Choose the duration</span> you would have preferred to maintain your
                                            investment. In the case of stock investments, it is typically recommended to
                                            remain invested for a minimum of five years.
                                        </li>
                                        <li>
                                            <span>Click the "Calculate"</span> button to estimate the potential income
                                            you could have gained from those investments if made in the past,
                                            considering the current date as the ending point.
                                        </li>


                                    </ul>
                                </Col>
                            </Row>


                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Others Year Modal */}
            <Modal show={showOthersModal} onHide={handleOthersModalClose} centered size="lg" dialogClassName="custom-modal-width">
                <Modal.Header >
                    <Modal.Title>Enter Custom Year</Modal.Title>
                    <button type="button" className="btn-close" aria-label="Close" onClick={handleOthersModalClose}>
                        <svg id="Button_-_Close" data-name="Button - Close"
                             xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 44 44">
                            <g id="Ellipse_18" data-name="Ellipse 18" fill="none" stroke="#3c3c3b"
                               strokeWidth="1" opacity="0.3">
                                <circle cx="22" cy="22" r="22" stroke="none"/>
                                <circle cx="22" cy="22" r="21.5" fill="none"/>
                            </g>
                            <g id="Ellipse_19" data-name="Ellipse 19" fill="none" stroke="#3c3c3b"
                               strokeWidth="1" strokeDasharray="0 142" opacity={'0'}>
                                <circle cx="22" cy="22" r="22" stroke="none"/>
                                <circle cx="22" cy="22" r="21.5" fill="none"/>
                            </g>
                            <g id="Group_18979" data-name="Group 18979" transform="translate(-3149 -104.5)">
                                <line id="Line_4" data-name="Line 4" x2="8" y2="8"
                                      transform="translate(3167 122.5)" fill="none" stroke="#3c3c3b"
                                      strokeLinecap="round" strokeWidth="1"/>
                                <line id="Line_3877" data-name="Line 3877" x1="8" y2="8"
                                      transform="translate(3167 122.5)" fill="none" stroke="#3c3c3b"
                                      strokeLinecap="round" strokeWidth="1"/>
                            </g>
                        </svg>

                    </button>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="modal-form-group">
                        <Form.Label>Enter a number between 1-15:</Form.Label>
                        <Form.Control
                            className="modal-form-control"
                            type="number"
                            min="1"
                            max="15"
                            value={customYear}
                            onChange={(e) => {
                                const value = e.target.value;
                                setCustomYear(value);

                                // Show error if value is greater than 15
                                if (value && parseInt(value) > 15) {
                                    setModalYearError('Please enter a number between 1-15');
                                } else {
                                    setModalYearError('');
                                }
                            }}
                            placeholder="Enter year (1-15)"
                        />
                        {modalYearError && (
                            <div className="modal-year-error">{modalYearError}</div>
                        )}
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <div className="close-modal" onClick={handleOthersModalClose}>
                        <Button
                            variant="secondary"

                            text="Cancel"
                            hoverColor="#6c757d"
                            hoverBackground="#6c757d"
                        />
                    </div>
                    <div className="calculate-modal" onClick={handleOthersYearSubmit}>
                        <Button
                            variant="primary"

                            text="Confirm"
                            hoverColor="#978C21"
                            hoverBackground="#978C21"
                            disabled={!customYear || parseInt(customYear) < 1 || parseInt(customYear) > 15}
                        />
                    </div>

                </Modal.Footer>
            </Modal>
        </StyledComponent>
    );
};


InvestorCalculator.getInitialProps = wrapper.getInitialPageProps((store) => async () => {
    let api_services = ApiServices.SECTIONS;
    let param = {
        [ApiParamKey.TYPE]: 'slug',
        [ApiParamKey.VALUE]: 'investment-calculator',
        [ApiParamKey.GET_SECTION]: 'no',
        [ApiParamKey.IMAGE]: 'no',
        [ApiParamKey.POST]: 'yes',
        [ApiParamKey.GALLERY]: 'no',
    }
    await store.dispatch(fetchPosts([api_services, param]));
});

const StyledComponent = styled.section`

    .chart-container{
        height: 90vh;
        @media(max-width: 767px){
            height: 400px;
        }
    }
    .form-wrapper{
        position: relative;
        .calculator-error-message{
            position: absolute;
            bottom: 0;
        }
        &.last{
            .calculator-error-message{
                bottom: 10px;
            }
            .form-select{
                margin-bottom: 40px;
            }
        }
    }
    .calculator-error-message{
        color: red;
        font-size: 12px;
    }
    .desktop-view {
        display: flex;
    }

    .desktop-view-blog {
        display: block;
    }

    .mobile-view {
        display: none;
    }

    @media (max-width: 767px) {
        .desktop-view, .desktop-view-blog {
            //display: none;
            flex-direction: column;
            .col-md-6{
                max-width: 100%;
                width: 100%;
            }
        }

        .mobile-view {
            display: block;
        }
    }

    .hints {
        font-size: 14px !important;
        color: gray !important;
        margin-bottom: 25px !important;
        margin-top: 5px;
        font-weight: 500;
    }

    .react-select__value-container {
        div:nth-of-type(5) {
            svg {
                display: none;
            }
        }
    }

    .custom {
        border: 1px solid black;
        border-collapse: collapse;

        td, th, td {
            border: 1px solid black;

        }
    }

    .modal-data__content {
        border-top: none;
    }

    .dc-btn a:hover {
        span {
            color: white;
        }
    }

    .form {
        input[type="Date"] {
            color: #3C3C3B;

        }

        .react-datepicker-wrapper {
            width: 100%;
        }

        .react-datepicker-popper[data-placement^=bottom] .react-datepicker__triangle::before {
            left: -25px;
        }

        .react-datepicker-popper[data-placement^=bottom] .react-datepicker__triangle::after {
            left: -25px;

        }

        .react-datepicker__input-container {
            width: 100%;

            input {
                width: 100%;

                font-size: 14px;
                font-weight: 500;
                color: #3C3C3B;
                border-right: none;
                border-left: none;
                border-top: none;
                outline: none;
                box-shadow: none;
                padding-left: 0;
                padding-right: 0;
                padding-bottom: 10px;
                padding-top: 0;
                height: 33px;
                margin-bottom: 25px;
                border-bottom: 1px solid rgba(60, 60, 59, 0.5);

                &::placeholder {
                    font-size: 14px;
                    font-weight: 500;
                    color: rgba(60, 60, 59, 0.5);
                }
            }

        }

        .form-control {
            font-size: 14px;
            font-weight: 500;
            color: #3C3C3B;
            border-right: none;
            border-left: none;
            border-top: none;
            outline: none;
            box-shadow: none;
            padding-left: 0;
            padding-right: 0;
            padding-bottom: 10px;
            padding-top: 0;
            height: 33px;
            margin-bottom: 25px;
            border-bottom: 1px solid rgba(60, 60, 59, 0.5);

            &::placeholder {
                font-size: 14px;
                font-weight: 500;
                color: rgba(60, 60, 59, 0.5);
            }
        }

        .react-select__indicator {
            padding: 0;
        }

        .react-select__placeholder {
            margin: 0;
            padding: 0;
            font-size: 14px;
            font-weight: 500;
            color: rgba(60, 60, 59, 0.5);
        }

        .react-select__control {
            border-bottom: 1px solid rgba(60, 60, 59, 0.5);
            margin-bottom: 0px;
            border-right: none;
            border-left: none;
            border-top: none;
            outline: none;
            box-shadow: none;
            padding-left: 0;
            padding-right: 0;

            .react-select__indicator-separator {
                display: none;
            }

            .react-select__input-container {
                padding: 0;
                margin: 0;
            }

            .react-select__single-value {
                margin: 0;
                padding: 0;
                font-size: 14px;
                font-weight: 500;
                color: #3C3C3B;
            }

            .react-select__value-container {
                padding-left: 0;
                padding-right: 0;
                padding-top: 0;
                padding-bottom: 0;
            }
        }
    }

    .result-wrp {
        p {
            color: rgba(60, 60, 59, 0.5);
            margin-bottom: 9px !important;

            &.last {
                margin-top: 30px;
            }
        }

        h3 {
            font-size: 32px;
            font-weight: 500;
            line-height: 44px;
            color: #978C21;
        }

        .calculation-note {
            margin-top: 20px !important;
            font-style: italic;

            small {
                color: rgba(60, 60, 59, 0.7) !important;
                font-size: 12px;
            }
        }

        &.error {
            p {
                color: #dc3545;
                font-weight: 500;
            }
        }
    }

    .simplebar-content {
        &::-webkit-scrollbar {
            display: none;
        }
    }

    .invest-content {
        margin-top: 80px;
        padding-top: 80px;
        //border-top: 1px;
        display: block;
        //border-style: solid;
        //border-color: #3C3C3B;
        border-bottom: none !important;

        ul {
            margin: 0;
            padding-left: 0;
            border: none !important;

            li {
                font-size: 16px;
                line-height: 24px;
                font-weight: 500;
                color: #3C3C3B;

                span {
                    font-family: "Avenir Heavy";
                }

                p {
                    font-size: 20px;
                    line-height: 24px;
                    font-weight: 500;
                    color: #3C3C3B;
                    margin: 0 !important;
                }

                padding: 0px 0 20px 35px !important;
                z-index: 50;
                position: relative;
                counter-increment: count;
                border-bottom: 1px solid rgba(198, 198, 198, 0.5);
                margin-bottom: 20px !important;

                &:first-child {
                    padding-top: 0;
                }

                &:last-child {
                    padding-bottom: 0;
                    border-bottom: 0;
                    margin-bottom: 0 !important;
                }

                &:first-child:before {
                    margin-top: 0;
                }

                &:last-child:before {
                    margin-bottom: 0;
                }

                &:after {
                    content: counter(count, upper-alpha) !important;
                    position: absolute !important;;
                    height: 20px !important;;
                    width: 20px !important;;
                    padding: 1px 0px 0px 0px !important;;
                    //margin: 35px 0;
                    font-size: 12px;
                    line-height: 20px;
                    font-weight: 500;
                    border: 1px solid #FFFFFF;
                    background-color: #978C21;
                    border-radius: 50%;
                    color: white !important;;
                    top: 0 !important;;
                    left: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
            }
        }

    }


    @media (max-width: 767px) {
        .scroll-div {
            padding: 0 !important;

        }

        .modal-data {
            margin-top: 0 !important;
        }

        .modal-close {
            right: 15px !important;
            //top: 0px !important;
        }

        .result {
            margin-top: 60px;
        }

        .invest-content {
            padding-top: 60px;
            margin-bottom: 60px;
        }

        .modal-data__content {
            margin-top: 60px !important;
            padding: 0 15px !important;
        }
    }

    .custom-modal-width {
        max-width: 45% !important;

        @media (max-width: 768px) {
            max-width: 90% !important;
        }
    }

    .modal {
        .modal-content {
            border-radius: 8px;
        }

        .modal-header {
            border-bottom: 1px solid #dee2e6;

            .modal-title {
                font-weight: 600;
                color: #3C3C3B;
            }
        }

        .modal-body {
            padding: 20px;

            .modal-form-group {
                margin-bottom: 0;
            }

            .form-label {
                font-size: 14px;
                font-weight: 500;
                color: #3C3C3B;
                margin-bottom: 15px;
                display: block;
            }

            .modal-form-control {
                font-size: 14px;
                font-weight: 500;
                color: #3C3C3B;
                border-right: none;
                border-left: none;
                border-top: none;
                outline: none;
                box-shadow: none;
                padding-left: 0;
                padding-right: 0;
                padding-bottom: 10px;
                padding-top: 0;
                height: 33px;
                margin-bottom: 25px;
                border-bottom: 1px solid rgba(60, 60, 59, 0.5);
                background: transparent;
                border-radius: 0;
                width: 100%;

                &::placeholder {
                    font-size: 14px;
                    font-weight: 500;
                    color: rgba(60, 60, 59, 0.5);
                }

                &:focus {
                    border-bottom: 1px solid #978C21;
                    box-shadow: none;
                    outline: none;
                }
            }

            .modal-year-error {
                color: red;
                font-size: 12px;
                margin-top: 5px;
            }
        }

        .modal-footer {
            border-top: 1px solid #dee2e6;
            padding: 15px 20px;

            .dc-btn {
                margin: 0 5px;
            }
        }
    }
`;

export default InvestorCalculator;